import abstractCaseTranslations from "./abstractCaseTranslations";
import abstractLitigationCaseTranslations from "./abstractLitigationCaseTranslations";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import personTranslations from "./personTranslations";
import organizationTranslations from "./organizationTranslations";
import addressTranslations from "./addressTranslations";
import bankDataTranslations from "./bankDataTranslations";

const employmentContractTranslations = {
  ...abstractCaseTranslations,
  ...abstractLitigationCaseTranslations,
  personalMessage: {
    stepHeadline: "Ihr Vertrag",
    headline: "Beschreibung der Situation",
    mainDescription: (caseData: AbstractCase) => {
      if (caseData.caseCategory !== "advisory") {
        return "Bitte schildern Sie uns hier Ihre Situation und Fragen. Jede Information hilft uns dabei, Ihren Fall besser einschätzen zu können. Im Anschluss können Sie ergänzend noch Dokumente oder Schriftverkehr mit dem Arbeitgeber hochladen, um Ihre Schilderungen zu ergänzen.";
      } else {
        return "Bitte schildern Sie uns hier ausführlich Ihre Situation und Ihre spezifischen Fragen. Jede Information hilft uns, Ihren arbeitsrechtlichen Fall besser einzuschätzen. Im Anschluss haben Sie die Möglichkeit, ergänzende Dokumente wie Arbeitsverträge, Schriftverkehr mit Ihrem Arbeitgeber oder andere relevante Unterlagen hochzuladen, um Ihre Schilderungen zu ergänzen.";
      }
    },
    label: "Ihre Situation und Fragen",
  },
  label: "Allgemeines Arbeitsrecht",
  caseCategory: {
    label: "Wie können wir Ihnen weiterhelfen?",
    options: {
      advisory: {
        label: "Ich benötige zunächst nur eine Erstberatung",
        optionHintText:
          "Ich möchte nichts weiter veranlassen, sondern habe nur eine kurze Frage im Bereich Arbeitsrecht und benötige hierfür eine Erstberatung.",
      },
      enforceClaim: {
        label: "Ich möchte eine Forderung durchsetzen",
        optionHintText:
          "Ich möchte meine Rechte aus meinem Arbeitsverhältnis einfordern. Beispiele: Ich verlange die Zahlung meines ausstehenden Lohns. | Ich möchte meine nicht bezahlten Überstunden oder meinen Urlaubsanspruch geltend machen. | Ich möchte gegen Mobbing vorgehen",
      },
      defendClaim: {
        label: "Ich möchte mich gegen eine Forderung wehren",
        optionHintText:
          "Von mir wird unberechtigterweise etwas eingefordert und ich möchte mich dagegen wehren. Beispiele: Der Arbeitgeber erteilt mir eine unberechtigte Abmahnung, fordert die Unterschrift unter einen Aufhebungsvertrag, eine bestimmte Arbeitsleistung oder Geld/Schadensersatz.",
      },
    },
  },
  affectedPartyType: {
    question: "Wer hat den Arbeitsvertrag geschlossen?",
    options: {
      customer: { label: "Ich habe den Vertrag selbst abgeschlossen" },
      coinsured: { label: "Der Vertrag wurde von jemand anderem abgeschlossen, der mir bekannt ist - zum Beispiel ein Familienmitglied" },
    },
  },
  currentlyEmployed: {
    question: "Stehen Sie zum jetzigen Zeitpunkt noch in einem Arbeitsverhältnis mit Ihrem Arbeitgeber?",
    options: {
      yes: { label: "Ja" },
      no: { label: "Nein" },
    },
  },
  employmentStartDate: {
    label: "Start Arbeitsverhältnis",
    question: "Bitte wählen Sie das genaue Datum aus, an dem Ihr Arbeitsverhältnis mit dem Arbeitgeber begonnen wurde.",
  },
  employmentEndDate: {
    label: "Ende Arbeitsverhältnis",
    question: "Bitte wählen Sie das genaue Datum aus, an dem Ihr Arbeitsverhältnis mit dem Arbeitgeber beendet wurde.",
  },
  employmentRegularWorkplace: {
    label: "Arbeitsort",
    question: "Wo haben Sie Ihre Arbeit gewöhnlich ausgeführt, oder wo war Ihr letzter regelmäßiger Arbeitsort?",
    options: {
      company: {
        label: "Am Hauptsitz des Arbeitgebers",
        optionHintText: "Der Arbeitsort entspricht dem Sitz des Arbeitgebers",
      },
      home: {
        label: "Zu Hause",
        optionHintText: "Ich habe regelmäßig zu Hause gearbeitet",
      },
      other: {
        label: "Anderer Ort",
        optionHintText: "Ich habe regelmäßig an einem anderen Ort, beispielsweise einer Zweigstelle oder woanders gearbeitet",
      },
    },
  },
  employmentRegularWorkplaceOtherAddress: addressTranslations,
  employmentRegularWorkplaceOtherAddressDescription: {
    label: "Beschreibung Arbeitsort",
    question: "Bitte geben Sie kurz den Arbeitsort an, an dem Sie Ihre Arbeit regelmäßig ausüben / ausgeübt haben",
  },
  subjectOfDispute: {
    label: "Forderung",
    question: (caseData: AbstractCase) =>
      caseData.caseCategory === "enforceClaim"
        ? "Möchten Sie Geld oder eine Leistung seitens des Arbeitgebers einfordern?"
        : "Was fordert Ihr Arbeitgeber von Ihnen?",
    options: {
      money: {
        label: (caseData: AbstractCase) => (caseData.caseCategory === "enforceClaim" ? "Ich möchte Geld einfordern" : "Der Arbeitgeber fordert Geld von mir"),
      },
      goods: {
        label: (caseData: AbstractCase) =>
          caseData.caseCategory === "enforceClaim"
            ? "Ich möchte eine Leistung oder ein gewisses Verhalten beim Arbeitgeber einfordern"
            : "Der Arbeitgeber fordert eine Leistung oder ein gewisses Verhalten von mir",
      },
      moneyAndGoods: {
        label: (caseData: AbstractCase) =>
          caseData.caseCategory === "enforceClaim"
            ? "Ich möchte Geld fordern und auch eine Leistung"
            : "Mein Arbeitgeber möchte Geld von mir und auch eine Leistung",
      },
      other: { label: "Ich fordere etwas ganz anderes" },
    },
  },
  claimedAmountClient: {
    label: "Forderung in €",
    question: (caseData: AbstractCase) =>
      (["reminder", "debtCollection"].includes(caseData.defenseProcessStatus)
        ? "Wie viel Geld fordert Ihr Arbeitgeber (ohne Mahn-/Inkassogebühren)?"
        : "Wie viel Geld fordert Ihr Arbeitgeber?") +
      " Sollten Sie die Forderung (noch) nicht genau kennen, geben Sie hier eine Schätzung oder einfach “0” an.",
    helperText:
      "Bitte geben Sie den Wert in Euro und Cent ein - zum Beispiel 136,15 oder 85,00. Bei Forderungen wie Lohn, Überstundenvergütung oder Urlaubsabgeltung ist immer der Bruttobetrag anzugeben. Wenn Sie den Betrag noch nicht genau wissen, lassen Sie das Feld einfach leer.",
  },
  client: {
    ...abstractCaseTranslations.client,
    bankData: {
      ...bankDataTranslations,
      iban: {
        question: "Wie lautet Ihre Bankverbindung, auf die der geforderte Betrag überwiesen werden soll?",
        helperText: "Wenn Sie die IBAN gerade nicht wissen, lassen Sie das Feld gerne einfach leer.",
        label: "IBAN",
      },
    },
  },
  reasonsForDispute: {
    label: "Problem mit Arbeitgeber",
    question: "Welches Anliegen haben Sie bezüglich Ihres Arbeitsvertrags?",
    options: {
      vacation: {
        label: (caseData: AbstractCase) => (caseData.subjectOfDispute !== "money" ? "Gewährung von Urlaub" : "Finanzielle Ansprüche aus Urlaub"),
        optionHintText: (caseData: AbstractCase) =>
          caseData.subjectOfDispute !== "money"
            ? "Ihr Arbeitgeber verweigert Ihnen die Gewährung Ihres Urlaubsanspruchs."
            : "Ihr Arbeitgeber verweigert Ihnen die Auszahlung offener Urlaubsansprüche.",
      },
      overtime: {
        label: (caseData: AbstractCase) =>
          caseData.subjectOfDispute !== "money" ? "Überstunden / Freizeitausgleich" : "Finanzielle Ansprüche aus Überstunden",
        optionHintText: "Ihr Arbeitgeber hat Ihre geleisteten Überstunden nicht vergütet oder als Freizeitausgleich gewährt.",
      },
      workCertificate: {
        label: "Arbeitszeugnis",
        optionHintText: "Ihr Arbeitgeber hat Ihnen kein Arbeitszeugnis ausgestellt oder das erhaltene Zeugnis ist fehlerhaft.",
      },
      parentalLeave: {
        label: "Elternzeit",
        optionHintText: "Ihr Arbeitgeber verweigert Ihnen die Inanspruchnahme oder Bestätigung Ihrer beantragten Elternzeit.",
      },
      partTime: {
        label: "Teilzeit",
        optionHintText: "Ihr Arbeitgeber lehnt Ihren Antrag auf Teilzeitarbeit ab oder gewährt sie nicht wie gewünscht.",
      },
      bullying: {
        label: "Mobbing / Diskriminierung",
        optionHintText: "Sie erleben systematische Schikanen (Mobbing) oder Ungleichbehandlung (Diskriminierung) am Arbeitsplatz.",
      },
      other: { label: "Anderes Anliegen", optionHintText: "Sie haben ein anderes Anliegen im Bereich Arbeitsrecht." },
      salary: {
        label: "Finanzielle Ansprüche aus Lohn",
        optionHintText: "Ihr Arbeitgeber hat Ihnen Lohn, Gehalt oder Prämien nicht oder nicht vollständig ausgezahlt",
      },
      claimedPayment: {
        label: "Geldrückforderung",
        optionHintText: "Der Arbeitgeber verlangt die Rückzahlung von zu viel gezahltem Lohn, Provisionen, Boni oder Fortbildungskosten.",
      },
      allegedDamage: {
        label: "Schadensersatzforderung",
        optionHintText:
          "Der Arbeitgeber macht Schadensersatzansprüche geltend, z.B. wegen Beschädigung von Firmeneigentum, Pflichtverletzungen oder Wettbewerbsverstößen.",
      },
      writtenWarning: {
        label: "Abmahnung",
        optionHintText:
          "Ihr Arbeitgeber hat Sie aufgrund eines angeblichen Fehlverhaltens abgemahnt. Beispiele: Der Vorwurf betrifft Unpünktlichkeit, Schlechtleistung oder einen Verstoß gegen Anweisungen.",
      },
      termination: {
        label: "Aufhebungsvertrag prüfen",
        optionHintText:
          "Ihr Arbeitgeber schlägt die Beendigung Ihres Arbeitsverhältnisses durch einen Aufhebungsvertrag vor. Beispiele: Es geht um die Höhe einer Abfindung, Freistellung von der Arbeit, den Verzicht auf Kündigungsschutzklage oder andere Bedingungen.",
      },
      itemsToReturn: {
        label: "Herausgabeansprüche",
        optionHintText:
          "Der Arbeitgeber fordert von Ihnen die Herausgabe von Gegenständen oder Unterlagen. Beispiele: Dies kann ein Dienstwagen, Laptop, Handy, Werkzeuge oder Unternehmensdokumente betreffen.",
      },
    },
  },
  salaryOutstandingStartDate: {
    label: "Lohn ausstehend seit",
    question:
      "Um Ihren Lohnanspruch genau zu erfassen, benötigen wir den Zeitraum, für den die Zahlungen ausstehen. Bitte geben Sie den Beginn und das Ende dieses Zeitraums an.",
  },
  salaryOutstandingEndDate: {
    label: "Lohn ausstehend bis",
    question: "",
  },
  grossSalary: {
    label: (caseData: AbstractCase)  => {
      return caseData.currentlyEmployed === "yes" ? "Aktueller Bruttomonatslohn" : "Letzter Bruttomonatslohn";
    },
    question:
      " Bitte tragen Sie hier Ihren Bruttomonatslohn ein, das ist der Betrag vor Abzug von Steuern und Sozialabgaben. Sie finden diese Angabe üblicherweise auf Ihren Gehaltsabrechnungen oder in Ihrem Arbeitsvertrag.",
    helperText: "Sollten Sie keine regelmäßige Lohnzahlung haben, so lassen Sie das Feld einfach leer",
  },
  overtimeAccumulatedStartDate: {
    label: "Überstunden angefallen seit",
    question: "Bitte geben Sie hier das Datum an, ab dem die Überstunden angefallen sind, die Sie geltend machen möchten.",
  },
  overtimeAccumulatedEndDate: {
    label: "Überstunden angefallen bis",
    question: "Bitte geben Sie hier das Datum an, bis zu dem die Überstunden angefallen sind, die Sie geltend machen möchten.",
  },
  overtimeAccumulatedTotal: {
    label: "Anzahl offene Überstunden",
    question: "Bitte tragen Sie hier die genaue Anzahl der Stunden ein, die Ihnen noch vergütet werden müssen.",
  },
  weeklyWorkingHours: {
    label: "Regelmäßige wöchentliche Arbeitszeit",
    question: "Um Ihren Stundenlohn zu ermitteln, benötigen wir Angaben zu Ihrer regelmäßigen wöchentlichen Arbeitszeit.",
    helperText: "Sollten Sie keine regelmäßige Arbeitszeit haben, so lassen Sie das Feld einfach leer",
  },
  hasOvertimeSurcharge: {
    label: "Überstundenzuschläge",
    question: "Gibt es Regelungen zu Überstundenzuschlägen?",
    options: {
      yes: { label: "Ja" },
      no: { label: "Nein" },
    },
  },
  overtimeSurchargeDetails: {
    label: "Zusatzregelungen Überstundenzuschläge",
    helperText:
      "Bitte geben Sie an, welche zusätzlichen Regelungen zu Überstundenzuschlägen in Ihrem Arbeitsvertrag, Tarifvertrag oder in einer Betriebsvereinbarung festgehalten sind.",
  },
  yearOfVacationClaim: {
    label: "Anspruch Urlaubsjahr",
    question: (caseData: AbstractCase) =>
      caseData.subjectOfDispute === "money"
        ? "Bitte wählen Sie das Urlaubsjahr aus, zu dem die offenen Urlaubstage gehören, die Sie abgelten möchten."
        : "Bitte wählen Sie das Urlaubsjahr aus, für das Sie noch Urlaub nehmen möchten.",
  },
  vacationDaysLeft: {
    label: "Anzahl offene Urlaubstage",
    question: (caseData: AbstractCase) =>
      caseData.subjectOfDispute === "money"
        ? "Bitte tragen Sie hier die genaue Anzahl der Urlaubstage ein, die Ihnen bei Beendigung des Arbeitsverhältnisses noch zustanden und nicht genommen werden konnten."
        : "Bitte tragen Sie hier die genaue Anzahl der Urlaubstage ein, die Ihnen noch zustehen und die Sie nehmen möchten.",
  },
  vacationDenialReason: {
    label: "Grund Ablehnung Urlaubsantrag",
    question: "Bitte wählen Sie den Grund aus, den Ihr Arbeitgeber für die Ablehnung Ihres Urlaubsantrags genannt hat.",
    options: {
      operationalNecessity: {
        label: "Dringende betriebliche Gründe",
        optionHintText: "Beispielsweise zu viele Kollegen im Urlaub oder hoher Arbeitsanfall",
      },
      noVacationDays: {
        label: "Kein bestehender Urlaubsanspruch",
        optionHintText: "Verfall durch Zeitablauf / Ausschlussfristen / Lange Krankheit",
      },
      requestNotOnTime: { label: "Antrag wurde nicht fristgerecht / zu kurzfristig gestellt" },
      noReasonProvided: {
        label: "Es wurde kein konkreter Grund genannt",
      },
      otherReason: {
        label: "Anderer Grund",
        optionHintText: "Sie können den Grund noch im Anschluss genauer erläutern.",
      },
    },
  },
  grossSalarySumLastThreeMonths: {
    label: "Summe Bruttoverdienst letzte 3 Monate",
    question:
      " Bitte tragen Sie hier die Gesamtsumme Ihrer Bruttoverdienste aus den letzten drei vollen Monaten vor Beendigung Ihres Arbeitsverhältnisses ein.",
  },
  hoursLeisureCompensation: {
    label: "Anzahl Stunden Freizeitausgleich",
    question: "Bitte tragen Sie hier die genaue Anzahl der Überstunden ein, die Sie als Freizeitausgleich erhalten möchten.",
  },
  hasOvertimeRecord: {
    label: "Anzahl geleisteten Überstunden",
    question:
      "Haben Sie Nachweise über die geleisteten Überstunden (z.B. Stundenzettel, Zeiterfassung)? Diese sind sehr hilfreich für die weitere Beweisführung.",
    options: {
      yes: { label: "Ja" },
      no: { label: "Nein" },
    },
  },
  workCertificateRequest: {
    label: "Anliegen Arbeitszeugnis",
    question: "Was ist Ihr Anliegen bezüglich Ihres Arbeitszeugnisses?",
    options: {
      requestIssuance: { label: "Ich möchte ein Arbeitszeugnis erhalten (habe noch keines bekommen)" },
      requestCorrection: { label: "Ich möchte mein bereits erhaltenes Arbeitszeugnis korrigieren lassen." },
    },
  },
  workCertificateIssuanceDenied: {
    label: "Ablehnung Ausstellung Arbeitszeugnis",
    question: "Hat Ihr Arbeitgeber die Erstellung des Zeugnisses bereits abgelehnt?",
    options: {
      yes: { label: "Ja" },
      no: { label: "Nein" },
    },
  },
  workCertificateIssuanceDenialReason: {
    label: "Begründung Ausstellungsverweigerung",
    question: "Mit welcher Begründung wurde die Ausstellung abgelehnt?",
    options: {
      noEntitlement: {
        label: "Anspruch wird bestritten",
        optionHintText: "Beispielsweise wegen zu kurzer Beschäftigungsdauer",
      },
      unresolvedDispute: {
        label: "Offene Streitigkeiten/Forderungen",
        optionHintText: "Arbeitgeber fordert zuerst Herausgabe von eingehaltenen Arbeitsmitteln oder will zuerst andere Streitigkeiten geregelt haben",
      },
      insufficientCapacity: { label: "Keine Zeit/Kapazitäten zur Erstellung" },
      noReasonProvided: { label: "Keine Begründung genannt" },
      other: { label: "Sonstiges", optionHintText: "Sie können den Grund noch im Anschluss genauer erläutern." },
    },
  },
  workCertificateReceivedAtDate: {
    label: "Arbeitszeugnis erhalten am",
    question: "Bitte geben Sie das Datum an, an dem Sie das Arbeitszeugnis erhalten haben.",
  },
  workCertificateCorrectionRequest: {
    label: "Zielsetzung",
    question: "Bitte geben Sie an, welche Korrekturen Sie wünschen.",
    options: {
      performanceEvaluation: { label: "Leistungsbeurteilung (Gesamtnote, Bewertung der Arbeitsleistung)" },
      conductEvaluation: { label: "Verhaltensbeurteilung (Umgang mit Kollegen, Vorgesetzten, Kunden)" },
      incompleteJobDescription: { label: "Fehlende/unvollständige Tätigkeitsbeschreibung" },
      missingKeyAchievements: { label: "Wichtige Erfolge/Aufgaben fehlen" },
      factualErrors: { label: "Faktische Fehler (z.B. falsche Daten, Positionsbezeichnung)" },
      formalErrors: { label: "Formale Fehler (z.B. fehlende Unterschrift, Datum, falscher Zeugnistyp)" },
      negativePhrasingOrCodes: { label: "Negative Formulierungen / Geheimcodes" },
      inadequateClosingStatement: { label: "Schlussformel (Dank, Bedauern, gute Wünsche) fehlt/ist unzureichend" },
      other: { label: "Sonstiges (Sie können den Grund noch im Anschluss genauer erläutern.)" },
    },
  },
  parentalLeaveStartDate: {
    label: "Beginn Elternzeit",
    question: "Bitte geben Sie hier das Datum an, ab wann Sie Elternzeit nehmen möchten.",
  },
  parentalLeaveEndDate: {
    label: "Ende Elternzeit",
    question: "Bitte geben Sie hier das Datum an, bis wann Sie Elternzeit nehmen möchten.",
  },
  hasTakenParentalLeaveBefore: {
    label: "Elternzeit bereits genommen?",
    question: "Haben Sie für dieses Kind oder ein anderes Kind bereits Elternzeit in Anspruch genommen?",
    options: {
      yes: { label: "Ja" },
      no: { label: "Nein" },
    },
  },
  parentalLeaveSplit: {
    label: "Aufteilung Elternzeit",
    question: "Wird die beantragte Elternzeit zwischen Ihnen und dem anderen Elternteil aufgeteilt?",
    options: {
      yes: { label: "Ja" },
      no: { label: "Nein" },
    },
  },
  parentalLeaveRequestOnTime: {
    label: " Elternzeit Antrag fristgerecht?",
    question: "Haben Sie Ihren Antrag auf Elternzeit fristgerecht (d.h. 7 bzw. 13 Wochen vor Beginn) bei Ihrem Arbeitgeber eingereicht?",
    options: {
      yes: { label: "Ja" },
      no: { label: "Nein" },
    },
  },
  parentalLeaveRequestDenied: {
    label: "Ablehnung Elternzeit erfolgt",
    question: "Wurde Ihr Antrag auf Elternzeit von Ihrem Arbeitgeber abgelehnt?",
    options: {
      yes: { label: "Ja" },
      no: { label: "Nein" },
    },
  },
  parentalLeaveRequestDeniedReason: {
    label: "Grund Ablehnung Elternzeit",
    question: "Bitte wählen Sie den Grund aus, den Ihr Arbeitgeber für die Ablehnung Ihres Elternzeitantrags genannt hat.",
    options: {
      requestNotOnTime: {
        label: "Antrag nicht fristgerecht gestellt",
        optionHintText: "Gesetzliche oder interne Fristen wurden nicht eingehalten.",
      },
      operationalNecessity: {
        label: "Dringende betriebliche Gründe",
        optionHintText: "Die Elternzeit würde den Betriebsablauf erheblich stören.",
      },
      formalErrors: {
        label: "Formalienfehler im Antrag",
        optionHintText: "Ihr Antrag weist Formfehler auf (z.B. fehlende Schriftform, Unterschrift oder Angaben).",
      },
      entitlementDisputedByEmployer: { label: "Arbeitgeber bestreitet Anspruch" },
      noReasonProvided: { label: "Keine Begründung genannt" },
      other: { label: "Sonstiges", optionHintText: "Sie können den Grund noch im Anschluss genauer erläutern." },
    },
  },
  partTimeStartDate: {
    label: "Start Teilzeit",
    question: "Bitte geben Sie das Datum an, ab dem Ihre gewünschte Teilzeit beginnen soll.",
  },
  partTimeDesiredWeeklyWorkingHours: {
    label: "Gewünschte Wochenstunden Teilzeit",
    question: "Bitte tragen Sie hier die Anzahl der Stunden ein, die Sie pro Woche arbeiten möchten.",
  },
  numberOfEmployees: {
    label: "Mitarbeiter im Unternehmen",
    question: "Bitte geben Sie die ungefähre Anzahl der Mitarbeiter an, die in Ihrem gesamten Betrieb (nicht nur in Ihrer Abteilung) arbeiten.",
  },
  partTimeDenialReason: {
    label: "Grund Ablehnung Teilzeit",
    question: "Aus welchem Grund wurde ihr Antrag auf Teilzeit abgelehnt?",
    options: {
      operationalReasons: {
        label: "Betriebliche Gründe",
        optionHintText: "Störung der Organisation oder des Arbeitsablaufs",
      },
      disproportionateCosts: { label: "Kosten sind unverhältnismäßig" },
      requirementsNotMet: {
        label: "Voraussetzungen nicht erfüllt",
        optionHintText: "Betrieb zu klein, zu kurze Betriebszugehörigkeit oder Frist nicht eingehalten",
      },
      formalErrors: {
        label: "Formalienfehler im Antrag",
        optionHintText: "Ihr Antrag weist Formfehler auf (z.B. fehlende Schriftform, Unterschrift oder Angaben).",
      },
      noReasonProvided: { label: "Keine Begründung genannt" },
      other: { label: "Sonstiges", optionHintText: "Sie können den Grund noch im Anschluss genauer erläutern." },
    },
  },
  incidentType: {
    label: "Vorfall",
    question: "Um welche Art von Vorfall handelt es sich?",
    options: {
      bullying: {
        label: "Mobbing",
        optionHintText:
          "Wichtig: Für die spätere rechtliche Einschätzung und Durchsetzung Ihres Anliegens ist es entscheidend, dass Sie diese Vorfälle sehr genau und detailliert dokumentieren, beispielsweise in einem Mobbingtagebuch.",
      },
      discrimination: {
        label: "Diskriminierung",
        optionHintText: "Ungleichbehandlung aufgrund von Merkmalen wie Geschlecht, Alter, Herkunft, Religion, Behinderung, sexueller Identität etc.",
      },
    },
  },
  incidentStartDate: {
    label: "Anfangszeitpunkt Mobbing/Diskriminierung",
    question: "Bitte geben Sie das Datum an, seit dem die Mobbing- oder Diskriminierungsvorfälle begonnen haben.",
    helperText: "Wenn Sie kein genaues Datum angeben können, lassen Sie das Feld einfach leer",
  },
  incidentObjectiveClient: {
    label: "Erhoffte Maßnahme",
    question: "Welche konkreten Maßnahmen möchten Sie erreichen?",
    options: {
      injunction: { label: "Unterlassung des Verhaltens" },
      compensation: { label: "Schadensersatz" },
      other: { label: "Sonstiges", optionHintText: "Sie können den Grund noch im Anschluss genauer erläutern." },
    },
  },
  opposingPartyType: {
    question:
      "Damit wir Ihre arbeitsrechtlichen Ansprüche erfolgreich durchsetzen können, benötigen wir möglichst genaue Angaben zu Ihrem Arbeitgeber. Diese Daten finden Sie in der Regel in Ihrem Arbeitsvertrag, auf Gehaltsabrechnungen oder in anderem Schriftverkehr. Bitte teilen Sie uns mit, ob Ihr Arbeitgeber eine Privatperson oder ein Unternehmen ist.",
    options: {
      opposingPartyLegalEntity: {
        label: "Unternehmen",
        question: "Wie heißt das Unternehmen, bei dem Sie angestellt sind?",
        helperText: "Bitte geben Sie die vollständige Firma an, die auch im Arbeitsvertrag genannt wird",
      },
      opposingPartyPerson: {
        label: "Privatperson",
        question: "Wie heißt ihr Arbeitgeber",
      },
    },
  },
  contractualPartnerHasLawyer: {
    label: "Arbeitgeber anwaltlich vertreten",
    question: "Gibt es einen Anwalt oder eine Anwaltskanzlei, die Ihren Arbeitgeber vertritt und mit der Sie entsprechend zu allen Anliegen kommunizieren? ",
    options: {
      yes: {
        label: "Ja",
      },
      no: {
        label: "Nein",
      },
    },
  },
  opposingPartyLegalEntity: {
    ...abstractLitigationCaseTranslations.opposingPartyLegalEntity,
    question: "Wie heißt das Unternehmen, bei dem Sie angestellt sind?",
  },
  opposingPartyPerson: {
    ...abstractLitigationCaseTranslations.opposingPartyPerson,
    question: "Wie heißt ihr Arbeitgeber",
  },
  opponentLawyer: {
    ...personTranslations,
    headline: "Wie heißt der Anwalt/die Anwaltskanzlei des Arbeitgebers?",
  },
  opponentLawFirm: {
    organization: {
      ...organizationTranslations,
      name: { label: "Name der Kanzlei" },
      address: {
        ...addressTranslations,
        headline: "Welche Kontaktdaten des Anwalts/der Anwaltskanzlei liegen Ihnen vor? Diese finden Sie zum Beispiel auf dem Schreiben im Briefkopf.",
      },
    },
  },
  defenseProcessStatus: {
    label: "Stand der Forderung",
    question: "Wie ist der Stand der Forderung gegen Sie gerade?",
    options: {
      ...abstractLitigationCaseTranslations.defenseProcessStatus.options,
      none: {
        label: "Ich habe bisher nichts von dem Arbeitgeber gehört, fürchte aber, dass dies noch passieren wird",
      },
      reminder: {
        label: "Ich habe ein Forderungsschreiben von dem Arbeitgeber erhalten",
      },
      debtCollection: {
        label: "Der Arbeitgeber hat ein Inkasso-Unternehmen eingeschaltet, von dem ich zur Zahlung aufgefordert oder gemahnt wurde",
      },
    },
  },
  claimedPaymentTypes: {
    label: "Art der Rückforderung",
    question: "Bitte wählen Sie aus, welche Art von Rückforderung der Arbeitgeber geltend macht.",
    options: {
      salary: { label: "Zu viel gezahlter Lohn/Gehalt" },
      bonus: { label: "Zu viel gezahlte Boni/Provisionen" },
      training: { label: "Rückzahlung von Fortbildungskosten" },
      loan: { label: "Rückzahlung von Darlehen/Vorschüssen" },
      other: { label: "Sonstiges (Sie können den Grund noch im Anschluss genauer erläutern.)" },
    },
  },
  claimedPaymentReason: {
    label: "Begründung Rückforderung Arbeitgeber",
    question: "Bitte beschreiben Sie kurz, wie der Arbeitgeber die Rückforderung begründet.",
  },
  allegedDamageTypes: {
    label: "Vorgeworfener Schaden Arbeitgeber",
    question: "Bitte wählen Sie aus, welche Art von Schaden oder Fehlverhalten Ihnen der Arbeitgeber vorwirft.",
    options: {
      damageOrLossOfProperty: { label: "Beschädigung/Verlust von Firmeneigentum" },
      breachOfNonCompete: { label: "Verstoß gegen Geheimhaltungspflicht" },
      breachOfConfidentiality: { label: "Verstoß gegen Wettbewerbsverbot" },
      poorPerformance: { label: "Schlechtleistung/Pflichtverletzung" },
      embezzlementOrTheft: { label: "Untreue/Diebstahl" },
      other: { label: "Sonstiges (Sie können den Grund noch im Anschluss genauer erläutern.)" },
    },
  },
  allegedDamageDate: {
    label: "Datum Vorfall Schadensersatz",
    question: "Bitte geben Sie das Datum an, an dem der Vorfall, der zur Schadensersatzforderung geführt hat, stattgefunden hat.",
  },
  writtenWarningReceivedDate: {
    label: " Abmahnung erhalten am",
    question: "Bitte wählen Sie das Datum aus, an dem Ihnen die Abmahnung zugestellt wurde.",
  },
  writtenWarningReason: {
    label: "Grund für Abmahnung",
    question: "Bitte fassen Sie kurz zusammen, welches Verhalten oder welcher Vorfall Ihnen in der Abmahnung vorgeworfen wird.",
  },
  writtenWarningIllegalReasons: {
    label: "Abmahnung unrechtmäßig weil",
    question: "Bitte wählen Sie die Gründe aus, warum Sie die Abmahnung für unrechtmäßig halten.",
    options: {
      factuallyIncorrect: { label: "Der Sachverhalt stimmt nicht " },
      disproportionateWarning: { label: "Die Abmahnung ist unverhältnismäßig" },
      formalErrors: { label: "Es gibt Formfehler (z.B. keine genaue Bezeichnung)" },
      other: { label: "Sonstiges (Sie können den Grund noch im Anschluss genauer erläutern.)" },
    },
  },
  terminationAgreementOfferDate: {
    label: "Datum Angebot Aufhebungsvertrag",
    question: "Wann wurde Ihnen der Aufhebungsvertrag angeboten?",
  },
  terminationAgreementDeadlineDate: {
    label: "Frist Unterschrift Aufhebungsvertrag",
    question: "Bitte geben Sie die Frist an, bis zu der Sie den Vertrag unterschreiben sollen.",
  },
  terminationAgreementIncludesSettlement: {
    label: "Abfindungszahlung im Aufhebungsvertrag",
    question: "Ist im Aufhebungsvertrag eine Abfindungszahlung vorgesehen?",
    options: {
      yes: {
        label: "Ja",
      },
      no: {
        label: "Nein",
      },
    },
  },
  offeredSettlementPayment: {
    label: "Angebotene Abfindung",
    question: "Bitte tragen Sie hier den Betrag der Ihnen angebotenen Abfindung ein.",
  },
  terminationAgreementObjectivesClient: {
    label: "Ziel Prüfung Aufhebungsvertrag",
    question: "Was ist Ihr primäres Ziel bei der Prüfung/Verhandlung des Aufhebungsvertrags?",
    options: {
      maximizeSettlement: { label: "Maximierung der Abfindung" },
      avoidUnemploymentPenalty: { label: "Vermeidung einer Sperrzeit beim Arbeitslosengeld" },
      securePositiveWorkCertificate: { label: "Positives Arbeitszeugnis sichern" },
      achieveImmediateRelease: { label: "Sofortige Freistellung erreichen" },
      negotiateOtherTerms: { label: "Andere Bedingungen verhandeln" },
      remainEmployed: { label: "Ich möchte den Vertrag nicht unterschreiben, sondern im Arbeitsverhältnis bleiben" },
      other: { label: "Sonstiges (Sie können den Grund noch im Anschluss genauer erläutern.)" },
    },
  },
  itemsToReturn: {
    label: "Forderung Herausgabe Arbeitgeber",
    question: "Um Ihnen bei der Abwehr des Herausgabeanspruchs helfen zu können, benötigen wir genaue Angaben zu den geforderten Gegenständen oder Unterlagen.",
    options: {
      companyCar: { label: "Dienstwagen" },
      laptopOrTablet: { label: "Laptop / Tablet Diensthandy" },
      companyPhone: { label: "Diensthandy" },
      keysOrAccessCards: { label: "Schlüssel / Zugangskarten" },
      toolsOrEquipment: { label: "Arbeitsgeräte / Werkzeuge" },
      companyDocuments: { label: "Unternehmensdokumente / Daten" },
      other: { label: "Sonstiges (Sie können den Grund noch im Anschluss genauer erläutern.)" },
    },
  },
  itemsToReturnReason: {
    label: "Begründung Herausgabeanspruch",
    question: "Bitte beschreiben Sie kurz, wie der Arbeitgeber die Herausgabe begründet.",
  },
  itemsToReturnRejectionReasons: {
    label: "Grund Ablehnung Forderung",
    question: "Bitte wählen Sie die Gründe aus, warum Sie die Forderung für unberechtigt halten.",
    options: {
      itemsBelongToMe: { label: "Die geforderten Gegenstände/Unterlagen gehören mir." },
      itemsNeededForWork: { label: "Ich benötige die Gegenstände/Unterlagen für meine Arbeit." },
      notContractuallyAgreed: { label: "Die Forderung ist nicht vertraglich vereinbart." },
      disproportionateClaim: { label: "Die Forderung ist unverhältnismäßig." },
      claimExpired: { label: "Die Forderung ist verjährt." },
      other: { label: "Sonstiges (Sie können den Grund noch im Anschluss genauer erläutern.)" },
    },
  },
};

export default employmentContractTranslations;
